import { ActionableNotification } from '@carbon/react';
import styles from './capture.module.scss';

interface CaptureErrorNotificationProps {
  /** Re-runs the capture compute for the current definition. */
  onRetry: () => void;
}

/**
 * Error surface for a failed capture compute. The compute is time-sensitive — a
 * large draft can exceed the request timeout (a 503) — so a failure MUST read as
 * an error with a recovery path, never as a legitimate empty capture. Rendering
 * the empty "No intersecting slots" state here would present a failed compute as
 * a truthful-looking zero, which is exactly the silent-zero the epic forbids.
 *
 * @param {CaptureErrorNotificationProps} props - The retry handler.
 * @return {JSX.Element} The actionable error notification.
 */
export function CaptureErrorNotification({
  onRetry,
}: CaptureErrorNotificationProps): JSX.Element {
  return (
    <ActionableNotification
      className={styles.banner}
      inline
      kind="error"
      // hasFocus off: its auto-focus would scroll/jump the form on every failure.
      hasFocus={false}
      lowContrast
      hideCloseButton
      title="Couldn't compute the capture preview"
      subtitle="The preview timed out or failed to load. Retry, or save the scenario and try again — a large draft can be slow to compute."
      actionButtonLabel="Retry"
      onActionButtonClick={onRetry}
    />
  );
}
